import styled from 'styled-components';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'shared/components/molecules/Dropdown';
import { ILocation, IPriceData, IQuote } from 'Quotes/types/store';
import { Loading } from '../Loading/Loading';
import { selectNNILabel, selectSelectedPrice } from 'Quotes/selectors';
import { ProductType } from 'Quotes/types/productTypes';
import {
  ADDRESS_NOT_LISTED_PLACEHOLDER,
  DEFAULT_PLACEHOLDER,
} from 'Location/AddressNotListed/addressNotListedMenu';
import { doRequest, Methods } from 'Request';
import { concatenatePAFAddress } from 'shared/utils/addresses/helperFunctions';
import { IPAFAddress, isPAFSite } from 'shared/types/postcodeResults';
import { setFullAddressAEndAction, setFullAddressBEndAction, updatePriceOrderable } from 'Quotes/actions';
import { Supplier } from 'Quotes/types/supplier';
import { objectToGetParams } from 'shared/utils/objectToGetParams';
import { getPortSpeedFromPort } from 'shared/utils/connectionCapacity';

export const NO_ADDRESSES_TEXT = 'There are no servicable addresses available at this postcode. Please select an alternative supplier or contact your account manager for more details.';
export const SELECT_ADDRESS_TEXT = 'This pricing is available at selected locations for this postcode. Please select a location:';
export const ADDRESS_NOT_LISTED_SELECTED_TEXT = 'If your address is not listed please select an alternative supplier or contact your account manager for more details.';
export const ITS_CHECKING_TEXT = 'Checking availability for selected address...';
export const ITS_CHECK_FAILED_TEXT = 'We could not verify ITS availability for the selected address right now. Please try again or select an alternative supplier.';

export const getItsProductName = (accessType: string | null | undefined): string => {
  if (accessType === Supplier.ITS_FIBREBRIGHT) {
    return 'FibreBright';
  }

  return 'FibreLight';
};

export const getItsNotAvailableText = (accessType: string | null | undefined): string =>
  `ITS ${getItsProductName(accessType)} is not available at the selected address. Please select an alternative address or option.`;

export const getItsAvailableText = (accessType: string | null | undefined): string =>
  `ITS ${getItsProductName(accessType)} is available at the selected address.`;

type AddressSource = 'ITS' | 'CITYFIBRE';
type ItsAvailabilityCache = Record<string, boolean | null>;
type ItsAvailabilityInFlight = Record<string, Promise<boolean | null>>;

export interface IAddressOption {
  label: string;
  value: string;
  address: any;
}

export interface SupplierAddressSelectorParams {
  isReadOnly: boolean;
  quote: IQuote;
}

const NOT_LISTED_OPTION: IAddressOption = {
  label: ADDRESS_NOT_LISTED_PLACEHOLDER,
  value: 'NA',
  address: { attributes: { address_not_listed: true } },
};

const formatLocationDetail = (location: ILocation): string => {
  const { fullAddress } = location;
  if (!fullAddress) return '';
  if (isPAFSite(fullAddress)) {
    return concatenatePAFAddress(fullAddress.attributes, false);
  }
  const attrs = fullAddress.attributes as any;
  return [attrs.address_1, attrs.address_2, attrs.town].filter(Boolean).join(', ');
};

const getAddressIdentifiers = (address: IAddressOption | ILocation | null) => {
  const get = (key: 'udprn' | 'uprn'): string | null => {
    if (!address) return null;
    if ('address' in address && 'attributes' in address.address)
      return (address as IAddressOption).address.attributes?.[key] ?? null;
    if ('fullAddress' in address && 'attributes' in (address.fullAddress ?? {}))
      return (((address as ILocation).fullAddress?.attributes as IPAFAddress)?.[key] ?? null);
    return null;
  };
  return { udprn: get('udprn'), uprn: get('uprn') };
};

const idsMatch = (a: IAddressOption | ILocation | null, b: IAddressOption | ILocation | null): boolean => {
  const aIds = getAddressIdentifiers(a);
  const bIds = getAddressIdentifiers(b);
  if (aIds.udprn && bIds.udprn) return aIds.udprn === bIds.udprn;
  if (aIds.uprn && bIds.uprn) return aIds.uprn === bIds.uprn;
  return false;
};

const sameAddressIdentifier = (left: IAddressOption | ILocation | null, right: IAddressOption | ILocation | null): boolean => {
  if (idsMatch(left, right)) return true;
  const leftIds = getAddressIdentifiers(left);
  const rightIds = getAddressIdentifiers(right);
  // Treat both sides without identifiers as already equivalent.
  return !leftIds.udprn && !leftIds.uprn && !rightIds.udprn && !rightIds.uprn;
};

const findOptionByIdentifiers = (
  options: IAddressOption[],
  address: ILocation | IAddressOption | null
): IAddressOption | null => {
  return options.find((option) => idsMatch(option, address)) ?? null;
};

const findNotListedOption = (options: IAddressOption[]): IAddressOption | null => {
  return options.find((it) => it.value === 'NA') ?? null;
};

export const buildUpdatedAddress = (currentFullAddress: any, selected: IAddressOption) => ({
  hasChanged: true,
  ...currentFullAddress,
  attributes: {
    ...currentFullAddress?.attributes,
    ...selected.address.attributes,
    uprn: selected.address.attributes.uprn,   // Explicitly set as missing field allows old value to be retained
    udprn: selected.address.attributes.udprn, // Explicitly set as missing field allows old value to be retained
    post_town: selected.address.attributes.post_town ?? selected.address.attributes.town,
    thoroughfare: selected.address.attributes.thoroughfare ?? selected.address.attributes.street,
    reference: null,
    colt_building_id: null,
    address_not_listed: selected.value === 'NA',
  },
});

const isEndOrderable = (show: boolean, detail: string, selected: IAddressOption | null): boolean => {
  if (!show) {
    return detail !== NO_ADDRESSES_TEXT;
  }

  return Boolean(selected && selected.value !== 'NA');
};

const withNotListedOption = (options: IAddressOption[]): IAddressOption[] => {
  if (options.length > 0 && !options.some((opt) => opt.value === 'NA')) {
    return [...options, NOT_LISTED_OPTION];
  }

  return options;
};

export const SupplierAddressSelectorContainer = ({
  isReadOnly,
  addressData,
  quote,
  price,
  itsAvailabilityCache,
  itsInFlight,
}: {
  isReadOnly: boolean;
  quote: IQuote;
  price: IPriceData;
  itsAvailabilityCache?: ItsAvailabilityCache;
  itsInFlight?: ItsAvailabilityInFlight;
  addressData: {
    endA: {
      addresses: IAddressOption[],
      show: boolean,
      title: string,
      detail: string,
    },
    endB: {
      addresses: IAddressOption[],
      show: boolean,
      title: string,
      detail: string,
    },
  }
}) => {
  const endAOptions = useMemo(() => withNotListedOption(addressData.endA.addresses), [addressData.endA.addresses]);
  const endBOptions = useMemo(() => withNotListedOption(addressData.endB.addresses), [addressData.endB.addresses]);

  const [selectedA, setSelectedA] = useState<IAddressOption | null>(null);
  const [selectedB, setSelectedB] = useState<IAddressOption | null>(null);
  const [itsAOnNetwork, setItsAOnNetwork] = useState<boolean | null>(null);
  const [itsBOnNetwork, setItsBOnNetwork] = useState<boolean | null>(null);
  const [itsAError, setItsAError] = useState<string | null>(null);
  const [itsBError, setItsBError] = useState<string | null>(null);
  const [isCheckingItsA, setIsCheckingItsA] = useState(false);
  const [isCheckingItsB, setIsCheckingItsB] = useState(false);
  const localItsAvailabilityCacheRef = useRef<ItsAvailabilityCache>({});
  const localItsInFlightRef = useRef<ItsAvailabilityInFlight>({});
  const availabilityCache = itsAvailabilityCache ?? localItsAvailabilityCacheRef.current;
  const inFlight = itsInFlight ?? localItsInFlightRef.current;
  const isItsA = getAddressSourceForEnd(price.a_end_access_type, price.a_end_product_name) === 'ITS';
  const isItsB = getAddressSourceForEnd(price.b_end_access_type, price.b_end_product_name) === 'ITS';

  const renderEnd = (
    endData: { show: boolean; title: string; detail: string },
    options: IAddressOption[],
    selected: IAddressOption | null,
    onChange: (option: IAddressOption | null) => void,
    itsMessage?: string | null
  ) => (
    <div style={{ maxWidth: '50%' }}>
      <h4>{endData.title}</h4>
      {endData.show ? (
        <>
          <Dropdown
            className='select'
            placeholder={DEFAULT_PLACEHOLDER}
            options={options}
            isDisabled={isReadOnly}
            value={selected}
            onChange={onChange}
            isClearable={true}
          />
          {selected?.value === 'NA' && <div>{ADDRESS_NOT_LISTED_SELECTED_TEXT}</div>}
          {itsMessage && <div>{itsMessage}</div>}
        </>
      ) : (
        <div>{endData.detail}</div>
      )}
    </div>
  );

  const getAvailability = async (params: {
    postcode: string;
    term: string;
    port: string;
    bandwidth: string;
    type: string;
    uprn?: string;
    latitude?: string;
    longitude?: string;
  }): Promise<boolean | null> => {
    const cacheKey = [
      params.postcode,
      params.term,
      params.port,
      params.bandwidth,
      params.type,
      params.uprn,
      params.latitude,
      params.longitude,
    ].join('|');

    if (Object.prototype.hasOwnProperty.call(availabilityCache, cacheKey)) {
      return availabilityCache[cacheKey];
    }

    if (!inFlight[cacheKey]) {
      inFlight[cacheKey] = doRequest({
        method: Methods.GET,
        path: `/availability/its?${objectToGetParams({
          postcode: params.postcode,
          term: params.term,
          port: params.port,
          bandwidth: params.bandwidth,
          type: params.type,
          uprn: params.uprn,
          latitude: params.latitude,
          longitude: params.longitude,
        })}`,
      })
        .then((isOnNetwork) => {
          const isAvailable = typeof isOnNetwork?.data?.is_available === 'boolean' ? isOnNetwork.data.is_available : null;
          availabilityCache[cacheKey] = isAvailable;
          delete inFlight[cacheKey];
          return isAvailable;
        })
        .catch((error) => {
          delete inFlight[cacheKey];
          throw error;
        });
    }

    return inFlight[cacheKey];
  };

  useEffect(() => {
    const selectedFromAByAddress = findOptionByIdentifiers(endAOptions, quote.location.aEnd);
    const selectedFromBByAddress = findOptionByIdentifiers(endBOptions, quote.location.bEnd);

    const quoteAIds = getAddressIdentifiers(quote.location.aEnd);
    const quoteBIds = getAddressIdentifiers(quote.location.bEnd);
    const quoteHasAIdentifier = Boolean(quoteAIds.udprn || quoteAIds.uprn);
    const quoteHasBIdentifier = Boolean(quoteBIds.udprn || quoteBIds.uprn);
    const aAttributes = (quote.location.aEnd.fullAddress?.attributes ?? {}) as { address_not_listed?: boolean };
    const bAttributes = (quote.location.bEnd.fullAddress?.attributes ?? {}) as { address_not_listed?: boolean };
    const quoteANotListed = Boolean(aAttributes.address_not_listed);
    const quoteBNotListed = Boolean(bAttributes.address_not_listed);

    const selectedFromA = selectedFromAByAddress
      ?? ((quoteHasAIdentifier || quoteANotListed) ? findNotListedOption(endAOptions) : null);
    const selectedFromB = selectedFromBByAddress
      ?? ((quoteHasBIdentifier || quoteBNotListed) ? findNotListedOption(endBOptions) : null);

    setSelectedA(selectedFromA);
    setSelectedB(selectedFromB);
  }, [endAOptions, endBOptions, quote.location.aEnd, quote.location.bEnd]);

  useEffect(() => {
    let cancelled = false;

    const checkEnd = async (
      selected: IAddressOption | null,
      isIts: boolean,
      postcode: string,
      accessType: string | null,
      setChecking: (value: boolean) => void,
      setOnNetwork: (value: boolean | null) => void,
      setError: (value: string | null) => void
    ) => {
      if (!isIts || !selected || selected.value === 'NA') {
        setChecking(false);
        setOnNetwork(null);
        setError(null);
        return;
      }

      setError(null);
      setChecking(true);

      try {
        const isOnNetwork = await getAvailability({
          postcode: (selected.address?.attributes?.postcode ?? postcode).trim(),
          term: price.term_length_in_months ?? quote.contractTerm,
          port: `${getPortSpeedFromPort(quote.bearer)}`,
          bandwidth: price.bandwidth ?? quote.bandwidth,
          type: accessType?.toLowerCase().replace('its_', '') ?? "",
          uprn: selected.address?.attributes?.uprn?.trim() || undefined,
          latitude: selected.address?.attributes?.latitude?.trim() || undefined,
          longitude: selected.address?.attributes?.longitude?.trim() || undefined,
        });

        if (cancelled)
          return;

        setChecking(false);
        setOnNetwork(isOnNetwork);
        setError(isOnNetwork === null ? ITS_CHECK_FAILED_TEXT : null);
      } catch (error) {
        if (cancelled) {
          return;
        }

        setChecking(false);
        setOnNetwork(null);
        setError(ITS_CHECK_FAILED_TEXT);
      }
    };

    checkEnd(
      selectedA,
      isItsA,
      quote.location.aEnd.postcode,
      price.a_end_access_type,
      setIsCheckingItsA,
      setItsAOnNetwork,
      setItsAError
    );
    checkEnd(
      selectedB,
      isItsB,
      quote.location.bEnd.postcode,
      price.b_end_access_type,
      setIsCheckingItsB,
      setItsBOnNetwork,
      setItsBError
    );

    return () => {
      cancelled = true;
    };
  }, [
    isItsA,
    isItsB,
    price.a_end_access_type,
    price.b_end_access_type,
    price.bandwidth,
    price.term_length_in_months,
    quote.bearer,
    quote.bandwidth,
    quote.contractTerm,
    quote.location.aEnd.postcode,
    quote.location.bEnd.postcode,
    selectedA,
    selectedB,
  ]);

  const dispatch = useDispatch();
  useEffect(() => {
    const applySelectedAddress = (
      selected: IAddressOption | null,
      endLocation: ILocation,
      setAddressAction: (address: any, openreachAddress: any) => any
    ) => {
      if (!selected || sameAddressIdentifier(selected, endLocation)) {
        return;
      }

      const updatedAddress = buildUpdatedAddress(endLocation.fullAddress, selected);
      dispatch(setAddressAction(updatedAddress, null));
    };

    applySelectedAddress(selectedA, quote.location.aEnd, setFullAddressAEndAction);
    applySelectedAddress(selectedB, quote.location.bEnd, setFullAddressBEndAction);
  }, [dispatch, quote.location.aEnd, quote.location.bEnd, selectedA, selectedB]);

  useEffect(() => {
    const orderableA = isEndOrderable(addressData.endA.show, addressData.endA.detail, selectedA);
    const orderableB = isEndOrderable(addressData.endB.show, addressData.endB.detail, selectedB);
    const itsOrderableA = !isItsA || (Boolean(selectedA && selectedA.value !== 'NA') && !isCheckingItsA && !itsAError && itsAOnNetwork === true);
    const itsOrderableB = !isItsB || (Boolean(selectedB && selectedB.value !== 'NA') && !isCheckingItsB && !itsBError && itsBOnNetwork === true);
    const orderable = orderableA && orderableB && itsOrderableA && itsOrderableB;

    if (price.is_price_orderable !== orderable) {
      dispatch(updatePriceOrderable(price.id, orderable))
    }
  }, [
    addressData.endA.detail,
    addressData.endA.show,
    addressData.endB.detail,
    addressData.endB.show,
    dispatch,
    price.id,
    price.is_price_orderable,
    selectedA,
    selectedB,
    isItsA,
    isItsB,
    isCheckingItsA,
    isCheckingItsB,
    itsAError,
    itsBError,
    itsAOnNetwork,
    itsBOnNetwork,
  ]);

  const getItsAvailabilityMessage = (
    isIts: boolean,
    accessType: string | null,
    endShow: boolean,
    selected: IAddressOption | null,
    isChecking: boolean,
    isOnNetwork: boolean | null,
    error: string | null
  ): string | null => {
    if (!isIts || !endShow || !selected || selected.value === 'NA') {
      return null;
    }

    if (isChecking) {
      return ITS_CHECKING_TEXT;
    }

    if (isOnNetwork === false) {
      return getItsNotAvailableText(accessType);
    }

    if (error) {
      return error;
    }

    if (isOnNetwork === true) {
      return getItsAvailableText(accessType);
    }

    return null;
  };

  const itsMessageA = getItsAvailabilityMessage(isItsA, price.a_end_access_type, addressData.endA.show, selectedA, isCheckingItsA, itsAOnNetwork, itsAError);
  const itsMessageB = getItsAvailabilityMessage(isItsB, price.b_end_access_type, addressData.endB.show, selectedB, isCheckingItsB, itsBOnNetwork, itsBError);

  return (
    <OuterContainer className="{isReadOnly ? 'readonly' : ''} m-2">
      <div className="header">
        <h3>Price Options</h3>
        <div>{SELECT_ADDRESS_TEXT}</div>
      </div>
      <div className='content'>
        <AddressContainer>
          {renderEnd(addressData.endA, endAOptions, selectedA, setSelectedA, itsMessageA)}

          <Arrow />

          {renderEnd(addressData.endB, endBOptions, selectedB, setSelectedB, itsMessageB)}
        </AddressContainer>
      </div>
    </OuterContainer>
  );
};

const loadAddresses = async (
  postcode: string,
  source: AddressSource | null
): Promise<IAddressOption[]> => {
  try {

    let url = '';
    const cleanedPostcode = postcode.replace(/\s+/g, '');
    if (source === 'ITS') {
      url = `/paf-addresses?location=${cleanedPostcode}`;
    } else if (source === 'CITYFIBRE') {
      url = `/availability/cityfibre/${cleanedPostcode}`;
    } else return [];

    const response = await doRequest({
      method: Methods.GET,
      path: url,
    });

    const addresses: IAddressOption[] = response.data.map((item: any) => ({
      address: item,
      value: item.attributes.uprn,
      label: [
        item.attributes.organisation_name,
        item.attributes.building_name,
        item.attributes.sub_building,
        item.attributes.building_number !== "0" ? item.attributes.building_number : null,
        item.attributes.street || item.attributes.thoroughfare,
        item.attributes.town || item.attributes.post_town,
        item.attributes.county,
        item.attributes.postcode,
      ]
        .filter(Boolean) // Remove null or undefined parts
        .join(', '), // Combine into a single string
    }));

    return addresses;

  } catch (error) {
    return [];
  }
};

const getAddressSourceForEnd = (
  accessType: string | null | undefined,
  productName: string | null | undefined
): AddressSource | null => {
  if (accessType === Supplier.ITS_FIBRELIGHT || accessType === Supplier.ITS_FIBREBRIGHT) {
    return 'ITS';
  }

  if (accessType === Supplier.CITYFIBRE && productName === 'National') {
    return 'CITYFIBRE';
  }

  return null;
};

interface IEndAddressData {
  addresses: IAddressOption[];
  show: boolean;
  detail: string;
}

interface IAddressDataCache {
  endA: IEndAddressData;
  endB: IEndAddressData;
}

const getAddressCacheKey = (
  aSource: AddressSource | null,
  aPostcode: string,
  bSource: AddressSource | null,
  bPostcode: string,
  productType: ProductType | null | undefined
): string => {
  return [
    aSource,
    aPostcode,
    bSource,
    bPostcode,
    productType,
  ].join('|');
};

const getAddressLookupKey = (postcode: string, source: AddressSource | null): string => {
  return `${source ?? ''}|${postcode.replace(/\s+/g, '').toUpperCase()}`;
};

const getAddressesForLookup = async (
  lookupKey: string,
  postcode: string,
  source: AddressSource | null,
  lookupCacheRef: React.MutableRefObject<Record<string, IAddressOption[]>>,
  inFlightLookupRef: React.MutableRefObject<Record<string, Promise<IAddressOption[]>>>
): Promise<IAddressOption[]> => {
  if (lookupCacheRef.current[lookupKey]) {
    return lookupCacheRef.current[lookupKey];
  }

  if (!inFlightLookupRef.current[lookupKey]) {
    inFlightLookupRef.current[lookupKey] = loadAddresses(postcode, source)
      .then((addresses) => {
        lookupCacheRef.current[lookupKey] = addresses;
        delete inFlightLookupRef.current[lookupKey];
        return addresses;
      })
      .catch((error) => {
        delete inFlightLookupRef.current[lookupKey];
        throw error;
      });
  }

  return inFlightLookupRef.current[lookupKey];
};

export const useSupplierAddressSelectorWithSelectedPrice = (quote: IQuote) => {
  const selectedPrice = useSelector(selectSelectedPrice);
  const itsAvailabilityCache = useRef<ItsAvailabilityCache>({});
  const itsInFlight = useRef<ItsAvailabilityInFlight>({});
  const primaryNNILabel = useSelector(selectNNILabel);
  const aSource = getAddressSourceForEnd(selectedPrice.a_end_access_type, selectedPrice.a_end_product_name);
  const bSource = getAddressSourceForEnd(selectedPrice.b_end_access_type, selectedPrice.b_end_product_name);
  const isSupported = aSource !== null || bSource !== null;
  const defaultDetailA = quote.productType === ProductType.P2NNI
    ? (primaryNNILabel ?? "")
    : formatLocationDetail(quote.location.aEnd);
  const defaultDetailB = formatLocationDetail(quote.location.bEnd);

  const [isLoading, setIsLoading] = useState(false);
  const [showA, setShowA] = useState(aSource !== null);
  const [showB, setShowB] = useState(bSource !== null);
  const [detailA, setDetailA] = useState(defaultDetailA);
  const [detailB, setDetailB] = useState(defaultDetailB);
  const [addressListA, setAddressListA] = useState<IAddressOption[]>([]);
  const [addressListB, setAddressListB] = useState<IAddressOption[]>([]);
  const addressCacheRef = useRef<Record<string, IAddressDataCache>>({});
  const lookupCacheRef = useRef<Record<string, IAddressOption[]>>({});
  const inFlightLookupRef = useRef<Record<string, Promise<IAddressOption[]>>>({});

  let endATitle = "Address at " + quote.location.aEnd.postcode;
  if (quote.productType === ProductType.P2NNI) endATitle = "NNI at " + quote.location.aEnd.postcode;
  let endBTitle = "Address at " + quote.location.bEnd.postcode;
  if (quote.productType === ProductType.DIA) endBTitle = "Direct Internet Access";
  if (quote.productType === ProductType.P2CCT) endBTitle = "Cloud Connect to " + quote.location.aEnd.cloudConnect.name;

  const addressData = {
    endA: { show: showA, addresses: addressListA, title: endATitle, detail: detailA },
    endB: { show: showB, addresses: addressListB, title: endBTitle, detail: detailB },
  };

  useEffect(() => {
    let cancelled = false;
    const applyAddressData = (data: IAddressDataCache) => {
      setShowA(data.endA.show);
      setShowB(data.endB.show);
      setDetailA(data.endA.detail);
      setDetailB(data.endB.detail);
      setAddressListA(data.endA.addresses);
      setAddressListB(data.endB.addresses);
    };

    const loadBothAddresses = async () => {
      const shouldShowA = aSource !== null;
      const shouldShowB = bSource !== null;
      const initialData: IAddressDataCache = {
        endA: { addresses: [], show: shouldShowA, detail: defaultDetailA },
        endB: { addresses: [], show: shouldShowB, detail: defaultDetailB },
      };
      const cacheKey = getAddressCacheKey(
        aSource,
        quote.location.aEnd.postcode,
        bSource,
        quote.location.bEnd.postcode,
        quote.productType
      );
      const cachedAddressData = addressCacheRef.current[cacheKey];

      if (cachedAddressData) {
        applyAddressData(cachedAddressData);
        setIsLoading(false);
        return;
      }

      applyAddressData(initialData);

      if (!shouldShowA && !shouldShowB) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const nextData: IAddressDataCache = {
        endA: { ...initialData.endA },
        endB: { ...initialData.endB },
      };
      const lookupKeyA = getAddressLookupKey(quote.location.aEnd.postcode, aSource);
      const lookupKeyB = getAddressLookupKey(quote.location.bEnd.postcode, bSource);
      const loadEnd = async (
        end: 'endA' | 'endB',
        lookupKey: string,
        postcode: string,
        source: AddressSource
      ) => {
        const addresses = await getAddressesForLookup(
          lookupKey,
          postcode,
          source,
          lookupCacheRef,
          inFlightLookupRef
        );

        if (addresses.length === 0) {
          nextData[end].show = false;
          nextData[end].detail = NO_ADDRESSES_TEXT;
        }

        nextData[end].addresses = addresses;
      };

      if (shouldShowA && shouldShowB && lookupKeyA === lookupKeyB) {
        const addresses = await getAddressesForLookup(
          lookupKeyA,
          quote.location.aEnd.postcode,
          aSource,
          lookupCacheRef,
          inFlightLookupRef
        );
        if (cancelled) {
          return;
        }

        if (addresses.length === 0) {
          nextData.endA.show = false;
          nextData.endB.show = false;
          nextData.endA.detail = NO_ADDRESSES_TEXT;
          nextData.endB.detail = NO_ADDRESSES_TEXT;
        }

        nextData.endA.addresses = addresses;
        nextData.endB.addresses = addresses;
      } else {
        const tasks: Promise<void>[] = [];

        if (shouldShowA) {
          tasks.push(loadEnd('endA', lookupKeyA, quote.location.aEnd.postcode, aSource));
        }

        if (shouldShowB) {
          tasks.push(loadEnd('endB', lookupKeyB, quote.location.bEnd.postcode, bSource));
        }

        await Promise.all(tasks);
        if (cancelled) {
          return;
        }
      }

      addressCacheRef.current[cacheKey] = nextData;
      applyAddressData(nextData);
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    loadBothAddresses();

    return () => {
      cancelled = true;
    };
  }, [
    aSource,
    bSource,
    quote.location.aEnd.postcode,
    quote.location.bEnd.postcode,
    quote.productType,
    defaultDetailA,
    defaultDetailB,
  ]);

  return {
    isLoading,
    isSupported,
    addressData,
    selectedPrice,
    itsAvailabilityCache: itsAvailabilityCache.current,
    itsInFlight: itsInFlight.current,
  };
};

const SupplierAddressSelector = (props: SupplierAddressSelectorParams) => {
  const addressSelector = useSupplierAddressSelectorWithSelectedPrice(props.quote);

  return (
    <Loading isLoading={addressSelector.isLoading && addressSelector.isSupported}>
      {addressSelector.isSupported && (
        <SupplierAddressSelectorContainer
          addressData={addressSelector.addressData}
          price={addressSelector.selectedPrice}
          quote={props.quote}
          isReadOnly={props.isReadOnly}
          itsAvailabilityCache={addressSelector.itsAvailabilityCache}
          itsInFlight={addressSelector.itsInFlight}
        />
      )}
    </Loading>
  );
};

const OuterContainer = styled.div`
  background: white;
  border-radius: 5px;
  box-shadow: ${(props) => props.theme.shadows.boxShadow};
  border: solid 0.5px ${(props) => props.theme.colours.grey10};
  .header {
    padding: 1em 1em 0.5em 1em;
  }
  .content {
    padding: 0em 1em 0.5em 1em;
  }
  &.readonly {
    opacity: 0.7
  }
`;

const AddressContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 1em;
  margin: 0.5em 0;
  background: ${(props) => props.theme.colours.primaryB1};
  color: white;
  border-radius: 0.5rem;
  .select {
    color: black;
  }
  > * {
    flex: 1;
  }
`;

const Arrow = ({ transparent = false }) => (
  <div style={{ flex: 0, margin: '0 1em' }}>
    <i className="material-icons" style={{ opacity: transparent ? 0 : 1 }}>trending_flat</i>
  </div>
);

export default SupplierAddressSelector;
